import { TablePage, Dialog, View } from 'web-mojo';
import { Route, RouteList } from '@portal/models/Route.js';
import ScheduleRouteDrawer from '@portal/routes/views/ScheduleRouteDrawer.js';
import './RouteView.js';

export default class ScheduledRoutesPage extends TablePage {
    constructor(options = {}) {
        const today = new Date().toISOString().slice(0, 10);

        super({
            title: 'Scheduled Routes',
            icon: 'bi-calendar2-week-fill',
            pageName: 'scheduled_routes',
            route: 'group/routes/schedule',
            permissions: ['routes', 'view_routes', 'manage_routes', 'manage_group'],
            requiresGroup: true,

            Collection: RouteList,

            defaultQuery: {
                sort: '-scheduled_date',
                size: 200,
                scheduled_date__gte: today,
                group: () => app.activeGroup?.id,
            },

            columns: [
                {
                    label: 'Date',
                    key: 'scheduled_date',
                    formatter: "default('—')",
                    sortable: true,
                },
                {
                    label: 'Route',
                    key: 'template.name',
                    formatter: "default('—')",
                },
                {
                    label: 'Operator',
                    key: 'operator',
                    formatter: (op) => op ? (op.display_name || op.user?.username || `Operator ${op.id}`) : 'Unassigned',
                    visibility: 'md',
                },
                {
                    label: 'Status',
                    key: 'status',
                    formatter: "badge:scheduled=secondary,in_progress=primary,completed=success,cancelled=dark,draft=light",
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Scheduled',   value: 'scheduled' },
                            { label: 'In Progress', value: 'in_progress' },
                            { label: 'Completed',   value: 'completed' },
                            { label: 'Cancelled',   value: 'cancelled' },
                        ]
                    }
                },
                {
                    label: 'Progress',
                    key: 'progress',
                    formatter: (val) => val?.total ? `${val.completed} / ${val.total}` : '—',
                    visibility: 'md',
                },
            ],

            viewDialogOptions: {
                header: false,
                size: 'lg',
                footer: false,
            },

            selectable: true,

            batchActions: [
                { label: 'Cancel', icon: 'bi bi-x-circle', action: 'cancel' },
                { label: 'Export', icon: 'bi bi-download', action: 'export' },
            ],
            searchable: false,
            sortable: true,
            filterable: true,
            paginated: false,

            showRefresh: true,
            addButtonLabel: 'Schedule Route',
            showExport: false,

            tableViewOptions: {
                hideActivePillNames: ['group'],
                onAdd: () => {
                    const drawer = new ScheduleRouteDrawer({});
                    drawer.on('scheduled', () => {
                        app.toast.success('Route scheduled');
                        this.collection.fetch();
                    });
                    Dialog.showDialog({
                        title: 'Schedule Route',
                        body: drawer,
                        size: 'md',
                        buttons: [
                            { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                            { text: 'Schedule Route', class: 'btn-primary',
                              handler: () => drawer.onActionSchedule()
                            },
                        ],
                    });
                },
            },

            tableOptions: {
                striped: false,
                bordered: false,
                hover: true,
                responsive: true,
            },

            contextMenu: [
                { icon: 'bi-eye', label: 'View Details',     action: 'view-route' },
                { icon: 'bi-person-check', label: 'Assign Operator',  action: 'assign-operator' },
                { icon: 'bi-x-circle', label: 'Cancel Route',     action: 'cancel-route', className: 'text-danger' },
            ],

            ...options,
        });

        this.operators = [];
    }

    async onInit() {
        await super.onInit();
    }

    async onActionBatchCancel() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        const confirmed = await Dialog.confirm({
            title: `Cancel ${selected.length} route${selected.length > 1 ? 's' : ''}`,
            message: 'Cancel selected routes? This cannot be undone.',
            confirmText: 'Cancel Routes',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;
        await Promise.all(selected.map(item => item.model.save({ cancel: 1 })));
        app.toast.success(`Cancelled ${selected.length} route${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }

    async onActionBatchExport() {
        this.tableView.collection.export();
    }

    // ── Row / context menu actions ────────────────────────────────────────────

    onActionView(event, el) {
        const id = el.getAttribute('data-id');
        const route = this.collection.get(id);
        if (!route) return;

        if (route.get('status') === 'in_progress') {
            app.navigate('?page=group/live');
            return;
        }
        // Let the framework open RouteView via VIEW_CLASS
        return super.onActionView(event, el);
    }

    onActionViewRoute(event, el) {
        this.onActionView(event, el);
    }

    async onActionAssignOperator(event, el) {
        const id = el.getAttribute('data-id');
        const route = this.collection.get(id);
        if (!route) return;

        if (this.operators.length === 0) {
            const r = await app.api.get('/api/routes/operator', {
                group: app.groupId(),
                status: 'active',
                size: 200,
            });
            if (r.success && r.data.status) this.operators = r.data.data || [];
        }

        const panel = new AssignOperatorPanel({ route, operators: this.operators });
        panel.on('assigned', () => {
            app.toast.success('Operator assigned');
            this.collection.fetch();
        });
        Dialog.showDialog({
            title: 'Assign Operator',
            body: panel,
            size: 'sm',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: 'Assign', class: 'btn-primary',
                  handler: () => panel.onActionSave()
                },
            ],
        });
    }

    async onActionCancelRoute(event, el) {
        const id = el.getAttribute('data-id');
        const route = this.collection.get(id);
        if (!route) return;

        const confirmed = await Dialog.confirm({
            title: 'Cancel Route',
            message: `Cancel "${route.get('template')?.name || 'this route'}" on ${route.get('scheduled_date')}? This cannot be undone.`,
            confirmText: 'Cancel Route',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;

        const response = await route.save({ cancel: 1 });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to cancel route');
            return;
        }
        app.toast.success('Route cancelled');
        this.tableView.collection.fetch();
    }

}

class AssignOperatorPanel extends View {
    constructor({ route, operators }) {
        super();
        this.route = route;
        this.operators = operators;
    }

    async onActionSave() {
        const operatorId = this.element.querySelector('[name=operator]')?.value;
        if (!operatorId) {
            app.toast.warning('Select an operator');
            return false;
        }

        const response = await this.route.save({
            assign_operator: parseInt(operatorId),
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to assign operator');
            return false;
        }
        this.emit('assigned');
        return true;
    }

    get template() {
        const options = this.operators.map(op =>
            `<option value="${op.id}">${op.display_name || op.id}</option>`
        ).join('');

        return `
            <div class="p-3">
                <div class="mb-3">
                    <label class="form-label">Operator</label>
                    <select class="form-select" name="operator">
                        <option value="">Select…</option>
                        ${options}
                    </select>
                </div>
            </div>
        `;
    }

    set template(v) { this._t = v; }
}
